import * as PropTypes from 'prop-types';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ChemicalStructureSaltsSampleData from 'bulk-import/derived-columns/ChemicalStructureSaltsSampleData';
import TerminalProbesSampleData from 'bulk-import/derived-columns/TerminalProbesSampleData';
import {ChooseImportLocationModalPaneConfiguration} from 'bulk-import/panes/ChooseImportLocationModalPane';
import {MapColumnNamesModalPaneConfig} from 'bulk-import/panes/MapColumnNamesModalPane';
import {MapDerivedColumnsModalPaneConfiguration} from 'bulk-import/panes/MapDerivedColumnsModalPane';
import {ShowErrorsModalPaneConfiguration} from 'bulk-import/panes/ShowErrorsModalPane';
import {StorableImportSettingsModalPaneConfiguration} from 'bulk-import/panes/StorableImportSettingsModalPane';
import {UploadSpreadsheetModalPaneConfiguration} from 'bulk-import/panes/UploadSpreadsheetModalPane';
import {BulkImportOptions} from 'bulk-import/SpreadsheetBulkImportStore';
import {
  LabelingStrategy,
  Registry,
  REGISTRY_SHAPE,
  SchemalessBulkImportSupportedOligoAwareTypes,
  SPREADSHEET_LABELING_STRATEGIES,
} from 'constants/RegistryConstants';
import {SAMPLE_TYPE_DISPLAY} from 'constants/SampleType';
import {
  getRestrictedSampleUsersDisplayText,
  getRestrictedSampleUsersHeaderText,
} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {DatasetConfigModalConfiguration} from 'lab-automation/settings/automation-output-file-config/DatasetConfigModal';
import {SetColumnsMapModalConfiguration} from 'lab-automation/settings/automation-output-file-config/SetColumnsMapModal';
import {ClientAutomationOutputFileConfigSpec} from 'lab-automation/settings/RunSchemaSpecConstants';
import {TypedValue} from 'notebook/tables/TableConstants';
import {ORDERED_INGREDIENT_UNITS_FOR_DISPLAY} from 'registry/mixtures/MixtureConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {assertNever} from 'util/Types';

export interface ValidateSpreadsheetForBulkImportResponse {
  itemNameErrors: Array<BulkImportError>;
  registryErrors: Array<BulkImportError>;
  registryWarnings: Array<BulkImportError>;
  tagErrors: Array<BulkImportError>;
  otherErrors: Array<BulkImportError>;
  otherWarnings: Array<BulkImportError>;
  studyWarnings: Array<StudyWarningBulkImportError>;
}

export interface SpreadsheetValidationError {
  row: number;
  errorCode: RowErrorCode;
  errorMessage: string;
  [key: string]: unknown;
  fieldName?: string;
  invalidTypedValue?: TypedValue;
}

export interface StudyWarningSpreadsheetValidationError extends SpreadsheetValidationError {
  sampleHash: string;
}

export interface SpreadsheetValidationErrorInfo {
  itemNameErrors: Array<SpreadsheetValidationError>;
  tagErrors: Array<SpreadsheetValidationError>;
  registryErrors: Array<SpreadsheetValidationError>;
  registryWarnings: Array<SpreadsheetValidationError>;
  otherErrors: Array<SpreadsheetValidationError>;
  otherWarnings: Array<SpreadsheetValidationError>;
  studyWarnings: Array<StudyWarningSpreadsheetValidationError>;
}

export const EMPTY_SPREADSHEET_VALIDATION_ERROR_INFO: SpreadsheetValidationErrorInfo = {
  itemNameErrors: [],
  otherErrors: [],
  otherWarnings: [],
  registryErrors: [],
  registryWarnings: [],
  studyWarnings: [],
  tagErrors: [],
};

export interface CompletedSpreadsheetValidationErrorInfo extends SpreadsheetValidationErrorInfo {
  state: TaskState.COMPLETED;
}

export type SpreadsheetBulkImportValidationErrorInfo =
  | {state: TaskState.STARTED}
  | {state: TaskState.FAILED}
  | CompletedSpreadsheetValidationErrorInfo;

export interface ErrorData extends SpreadsheetValidationErrorInfo {
  state?: TaskState;
}

export enum TaskState {
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum SpreadsheetValidationErrorType {
  ITEM_NAME_ERRORS = 'itemNameErrors',
  TAG_ERRORS = 'tagErrors',
  REGISTRY_ERRORS = 'registryErrors',
  REGISTRY_WARNINGS = 'registryWarnings',
  OTHER_ERRORS = 'otherErrors',
  OTHER_WARNINGS = 'otherWarnings',
}

// Keep in sync with benchling/bulk_adapters/column_type.py::ColumnTypes
// Keep in sync with benchling/bulk_adapters/saved_column_mappings/models/individual_column_mapping.py::SpreadsheetColumnsMappingsColumnType
// Add new column types to COLUMN_TYPE_TO_NAME, COLUMN_TYPE_TO_HELP_TEXT, and COLUMN_TYPE_TO_SORT_ORDER below
export enum COLUMN_TYPE {
  ALIAS = 'ALIAS',
  DATASET = 'DATASET',
  DESCRIPTION = 'DESCRIPTION',
  FILENAME = 'FILENAME',
  NEW_BIOENTITY_NAME = 'NEW_BIOENTITY_NAME',
  NEW_REGISTRY_ID = 'NEW_REGISTRY_ID',
  FOLDER_PATH_FORWARD_SLASH = 'FOLDER_PATH_FORWARD_SLASH',
  IGNORE = 'IGNORE',
  MY_COLLECTION_ID = 'MY_COLLECTION_ID',
  ORG_COLLECTION_ID = 'ORG_COLLECTION_ID',
  BATCH_NAME = 'BATCH_NAME',
  HELM = 'HELM',
  IDT = 'IDT',
  CUSTOM_MOD_SYNTAX = 'CUSTOM_MOD_SYNTAX',
  RESIDUES = 'RESIDUES',
  CUSTOM_FIELD = 'CUSTOM_FIELD',
  SCHEMA_FIELD = 'SCHEMA_FIELD',
  SAMPLE_GROUP_SCHEMA_FIELD = 'SAMPLE_GROUP_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to the server
  RESULT_SCHEMA_FIELD = 'RESULT_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to server
  PROCESS_TASK_SCHEMA_FIELD = 'PROCESS_TASK_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to server
  PROCESS_OUTPUT_SCHEMA_FIELD = 'PROCESS_OUTPUT_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to server
  SEQUENCE_TOPOLOGY = 'SEQUENCE_TOPOLOGY',

  // Chemistry
  CAPILLARY_CHEMICAL_STRUCTURE_INPUT = 'CAPILLARY_CHEMICAL_STRUCTURE_INPUT',
  SMILES = 'SMILES',
  MOLFILE = 'MOLFILE',

  // Storables
  NUMBER_OF_CONTAINERS = 'NUMBER_OF_CONTAINERS',
  BARCODE = 'BARCODE',
  NEW_BARCODE = 'NEW_BARCODE',
  STORABLE_NAME = 'STORABLE_NAME',
  PARENT_STORABLE_BARCODE = 'PARENT_STORABLE_BARCODE',
  PARENT_STORABLE_GRID_COORDINATES = 'PARENT_STORABLE_GRID_COORDINATES',
  CONTAINER_SCHEMA_FIELD = 'CONTAINER_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to the server
  PLATE_SCHEMA_FIELD = 'PLATE_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to the server
  BOX_SCHEMA_FIELD = 'BOX_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to the server
  LOCATION_SCHEMA_FIELD = 'LOCATION_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to the server

  // Expiration & freeze/thaw
  EXPIRATION_DATE_OVERRIDE = 'EXPIRATION_DATE_OVERRIDE',
  FREEZE_THAW_COUNT = 'FREEZE_THAW_COUNT',

  // Location capacity configuration
  ALLOWS_NON_LOCATION_CHILDREN = 'ALLOWS_NON_LOCATION_CHILDREN',
  LOCATION_CAPACITY = 'LOCATION_CAPACITY',
  ALLOWED_CHILD_STORABLE_SCHEMAS = 'ALLOWED_CHILD_STORABLE_SCHEMAS',

  // Plates
  //
  // Input
  INPUT_CONTAINER_BARCODE = 'INPUT_CONTAINER_BARCODE',
  INPUT_BIOENTITY_LABEL = 'INPUT_BIOENTITY_LABEL',
  INPUT_BATCH_NAME = 'INPUT_BATCH_NAME',
  INPUT_GRID_BARCODE = 'INPUT_GRID_BARCODE',
  INPUT_GRID_COORDINATES = 'INPUT_GRID_COORDINATES',
  // Output
  OUTPUT_CONTAINER_BARCODE = 'OUTPUT_CONTAINER_BARCODE',
  OUTPUT_GRID_BARCODE = 'OUTPUT_GRID_BARCODE',
  OUTPUT_GRID_COORDINATES = 'OUTPUT_GRID_COORDINATES',
  // Fields
  INPUT_CONCENTRATION_VALUE = 'INPUT_CONCENTRATION_VALUE',
  CONCENTRATION_UNITS = 'INPUT_CONCENTRATION_UNITS', // Used for both input & final concentrations
  FINAL_CONCENTRATION_VALUE = 'FINAL_CONCENTRATION_VALUE',
  TRANSFER_QUANTITY = 'TRANSFER_VOLUME_AMOUNT',
  FINAL_QUANTITY_VALUE = 'FINAL_VOLUME_VALUE',
  SOURCE_FINAL_QUANTITY_VALUE = 'SOURCE_FINAL_VOLUME_VALUE',
  QUANTITY_UNITS = 'VOLUME_UNITS',

  // Inventory table
  ADD_DEDUCT_AMOUNT = 'ADD_DEDUCT_AMOUNT',
  // Sample control roles
  SAMPLE_OWNERS = 'SAMPLE_OWNERS',
  SAMPLE_RESTRICTION_STATUS = 'SAMPLE_RESTRICTION_STATUS',
  SAMPLE_AUTHORIZED_USERS = 'SAMPLE_AUTHORIZED_USERS',

  // Registration table
  STAGE_RUN_NAME = 'STAGE_RUN_NAME',
  REGISTRATION_TABLE_EXISTING_BIOENTITY = 'REGISTRATION_TABLE_EXISTING_BIOENTITY',

  // Data table
  TABLE_COLUMN = 'TABLE_COLUMN',
  DATA_TABLE_MATCHER = 'DATA_TABLE_MATCHER',
  DATA_TABLE_VALUE = 'DATA_TABLE_VALUE',

  // Transfer table
  STAGE_RUN_SAMPLE_NAME = 'STAGE_RUN_SAMPLE_NAME',

  // Mixture ingredients
  INGREDIENT_MIXTURE_LABEL = 'INGREDIENT_MIXTURE_LABEL',
  INGREDIENT_COMPONENT_BIOENTITY_LABEL = 'INGREDIENT_COMPONENT_BIOENTITY_LABEL',
  INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL = 'INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL',
  INGREDIENT_CATALOG_IDENTIFIER = 'INGREDIENT_CATALOG_IDENTIFIER',
  // TODO(BNCH-17431): String value left as INGREDIENT_SOURCE for now for backwards-compatibility
  INGREDIENT_COMPONENT_LOT_TEXT = 'INGREDIENT_SOURCE',
  INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL = 'INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL',
  INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE = 'INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE',
  INGREDIENT_AMOUNT = 'INGREDIENT_AMOUNT',
  INGREDIENT_UNITS = 'INGREDIENT_UNITS',
  INGREDIENT_NOTES = 'INGREDIENT_NOTES',

  // Processes
  PROCESS_TASK_ASSIGNEE = 'PROCESS_TASK_ASSIGNEE',
  PROCESS_TASK = 'PROCESS_TASK',

  // Procedures
  PROCEDURE_CONDITION_REPLICATES = 'PROCEDURE_CONDITION_REPLICATES',

  // Etc
  UNIT_SELECTOR = 'UNIT_SELECTOR',

  // Complex polymers
  COMPLEX_POLYMER_COMPONENT = 'COMPLEX_POLYMER_COMPONENT',
  COMPLEX_POLYMER_CONNECTION = 'COMPLEX_POLYMER_CONNECTION',
  OLIGO_DUPLEX_OFFSET = 'OLIGO_DUPLEX_OFFSET',

  // Studies
  STUDY_LABELS = 'STUDY_LABELS',

  // Trial subjects
  TRIAL_SUBJECT_NAME = 'TRIAL_SUBJECT_NAME',
  TRIAL_SUBJECT_SCHEMA_FIELD = 'TRIAL_SUBJECT_SCHEMA_FIELD', // Sent as SCHEMA_FIELD to the server
  TRIAL_SUBJECT_MY_COLLECTION_ID = 'TRIAL_SUBJECT_MY_COLLECTION_ID',
  TRIAL_SUBJECT_ORG_COLLECTION_ID = 'TRIAL_SUBJECT_ORG_COLLECTION_ID',
}

export const COLUMN_TYPE_TO_NAME: {[key in COLUMN_TYPE]?: string} = {
  [COLUMN_TYPE.ALIAS]: 'Additional alias',
  [COLUMN_TYPE.DESCRIPTION]: 'Description tab text',
  [COLUMN_TYPE.FILENAME]: 'Entity name, alias, or registry ID',
  [COLUMN_TYPE.NEW_BIOENTITY_NAME]: 'New entity name',
  [COLUMN_TYPE.NEW_REGISTRY_ID]: 'New registry ID',
  [COLUMN_TYPE.FOLDER_PATH_FORWARD_SLASH]: 'New folder path',
  [COLUMN_TYPE.MY_COLLECTION_ID]: 'Registry ID for my collection',
  [COLUMN_TYPE.ORG_COLLECTION_ID]: 'Registry ID for org collection',
  [COLUMN_TYPE.BATCH_NAME]: 'Batch ID',
  [COLUMN_TYPE.HELM]: 'HELM notation',
  [COLUMN_TYPE.IDT]: 'IDT notation',
  [COLUMN_TYPE.CUSTOM_MOD_SYNTAX]: 'Custom notation',
  [COLUMN_TYPE.RESIDUES]: 'Residues',
  [COLUMN_TYPE.SEQUENCE_TOPOLOGY]: 'Topology',
  [COLUMN_TYPE.IGNORE]: 'Ignore',
  [COLUMN_TYPE.CUSTOM_FIELD]: 'Custom field',
  [COLUMN_TYPE.SCHEMA_FIELD]: 'Entity field',
  [COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD]: 'Sample',
  [COLUMN_TYPE.RESULT_SCHEMA_FIELD]: 'Result field',
  [COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD]: 'Task field',
  [COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD]: 'Output field',
  // Storables
  [COLUMN_TYPE.STORABLE_NAME]: 'Name',
  [COLUMN_TYPE.BARCODE]: 'Barcode',
  [COLUMN_TYPE.NEW_BARCODE]: 'New barcode',
  [COLUMN_TYPE.PARENT_STORABLE_BARCODE]: 'Location barcode',
  [COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES]: 'Location position',
  [COLUMN_TYPE.CONTAINER_SCHEMA_FIELD]: 'Container field',
  [COLUMN_TYPE.PLATE_SCHEMA_FIELD]: 'Plate field',
  [COLUMN_TYPE.BOX_SCHEMA_FIELD]: 'Box Field',
  [COLUMN_TYPE.LOCATION_SCHEMA_FIELD]: 'Location field',
  [COLUMN_TYPE.NUMBER_OF_CONTAINERS]: 'Number of containers',
  [COLUMN_TYPE.EXPIRATION_DATE_OVERRIDE]: 'Expiration date override',
  [COLUMN_TYPE.FREEZE_THAW_COUNT]: 'Freeze/thaw count',
  // Location capacity configuration
  [COLUMN_TYPE.ALLOWS_NON_LOCATION_CHILDREN]: 'Allows plates, boxes, and containers',
  [COLUMN_TYPE.LOCATION_CAPACITY]: 'Capacity',
  [COLUMN_TYPE.ALLOWED_CHILD_STORABLE_SCHEMAS]: 'Allowed inventory schemas',
  // Transfers
  [COLUMN_TYPE.INPUT_CONTAINER_BARCODE]: 'Source container barcode',
  [COLUMN_TYPE.INPUT_BIOENTITY_LABEL]: 'Source entity name, alias, or registry ID',
  [COLUMN_TYPE.INPUT_BATCH_NAME]: 'Source batch ID',
  [COLUMN_TYPE.INPUT_GRID_BARCODE]: 'Source plate/box barcode',
  [COLUMN_TYPE.INPUT_GRID_COORDINATES]: 'Source position',
  [COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE]: 'Destination container barcode',
  [COLUMN_TYPE.OUTPUT_GRID_BARCODE]: 'Destination plate/box barcode',
  [COLUMN_TYPE.OUTPUT_GRID_COORDINATES]: 'Destination well',
  [COLUMN_TYPE.INPUT_CONCENTRATION_VALUE]: 'Source concentration',
  [COLUMN_TYPE.CONCENTRATION_UNITS]: 'Concentration units',
  [COLUMN_TYPE.FINAL_CONCENTRATION_VALUE]: 'Concentration',
  [COLUMN_TYPE.TRANSFER_QUANTITY]: `Transfer ${getQuantityDisplayString(false)}`,
  [COLUMN_TYPE.FINAL_QUANTITY_VALUE]: `Final ${getQuantityDisplayString(false)}`,
  [COLUMN_TYPE.SOURCE_FINAL_QUANTITY_VALUE]: `Source final ${getQuantityDisplayString(false)}`,
  [COLUMN_TYPE.QUANTITY_UNITS]: `${getQuantityDisplayString()} units`,
  // Inventory table
  [COLUMN_TYPE.ADD_DEDUCT_AMOUNT]: `Add/Deduct ${getQuantityDisplayString(false)}`,
  // Sample control roles
  [COLUMN_TYPE.SAMPLE_OWNERS]: 'Sample owners',
  [COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS]: 'Restriction status',
  [COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS]: getRestrictedSampleUsersHeaderText(),
  // Registration table
  [COLUMN_TYPE.STAGE_RUN_NAME]: 'Stage run',
  [COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY]: 'Existing entity name, alias, or registry ID',
  // Data table
  [COLUMN_TYPE.DATA_TABLE_VALUE]: 'Set value',
  [COLUMN_TYPE.DATA_TABLE_MATCHER]: 'Match value',
  // Transfer table
  [COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME]: 'Sample ID',
  // Mixture ingredients
  [COLUMN_TYPE.INGREDIENT_MIXTURE_LABEL]: 'Mixture - name, alias, or registry ID',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_BIOENTITY_LABEL]:
    'Ingredient component entity - name, alias, or registry ID',
  [COLUMN_TYPE.INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL]:
    'Ingredient updated component entity - name, alias, or registry ID',
  [COLUMN_TYPE.INGREDIENT_CATALOG_IDENTIFIER]: 'Ingredient catalog identifier',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_TEXT]: 'Ingredient component lot text',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL]:
    'Ingredient component lot entity - name, alias, or registry ID',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE]: 'Ingredient component lot container barcode',
  [COLUMN_TYPE.INGREDIENT_AMOUNT]: 'Ingredient amount',
  [COLUMN_TYPE.INGREDIENT_UNITS]: 'Ingredient units',
  [COLUMN_TYPE.INGREDIENT_NOTES]: 'Ingredient notes',
  // Processes
  [COLUMN_TYPE.PROCESS_TASK_ASSIGNEE]: 'Task assignee',
  [COLUMN_TYPE.PROCESS_TASK]: 'Task',
  // Procedures
  [COLUMN_TYPE.PROCEDURE_CONDITION_REPLICATES]: 'Condition - Replicate',
  // Chemistry
  [COLUMN_TYPE.SMILES]: 'SMILES',
  [COLUMN_TYPE.MOLFILE]: 'Molfile',
  // Complex polymers
  [COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT]: 'Oligo duplex or conjugate component',
  [COLUMN_TYPE.COMPLEX_POLYMER_CONNECTION]: 'Oligo duplex or conjugate connection',
  [COLUMN_TYPE.OLIGO_DUPLEX_OFFSET]: 'Forward offset',
  // Studies
  [COLUMN_TYPE.STUDY_LABELS]: 'Study ID',
  // Trial subjects
  [COLUMN_TYPE.TRIAL_SUBJECT_NAME]: 'Trial subject name',
  [COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD]: 'Trial subject field',
  [COLUMN_TYPE.TRIAL_SUBJECT_MY_COLLECTION_ID]: 'Trial subject registry ID for my collection',
  [COLUMN_TYPE.TRIAL_SUBJECT_ORG_COLLECTION_ID]: 'Trial subject registry ID for org collection',
};

export const COLUMN_TYPE_TO_HELP_TEXT: {[key in COLUMN_TYPE]?: string} = {
  // Help text for the following COLUMN_TYPES are not included:
  // RESIDUES (generated in SpreadsheetBulkImportUtil.getFieldHelpText)
  [COLUMN_TYPE.ALIAS]: 'An alternate name that will be added to the entity.',
  [COLUMN_TYPE.DESCRIPTION]: "Text to add to the entity's description tab.",
  [COLUMN_TYPE.FILENAME]: 'The name, alias, or registry ID used to identify the entity to be updated.',
  [COLUMN_TYPE.NEW_BIOENTITY_NAME]: 'The new name for the entity.',
  [COLUMN_TYPE.NEW_REGISTRY_ID]: 'The new registry ID for the entity',
  [COLUMN_TYPE.FOLDER_PATH_FORWARD_SLASH]:
    'The path of the folder that entities will be moved to, such as "project folder/sub folder/destination folder".',
  [COLUMN_TYPE.MY_COLLECTION_ID]: 'A unique alphanumeric value to identify the entity within the collection.',
  [COLUMN_TYPE.ORG_COLLECTION_ID]:
    'A unique alphanumeric value to identify the entity within the collection.',
  [COLUMN_TYPE.HELM]: 'A HELM string specifiying bases and modifications of an oligo.',
  [COLUMN_TYPE.IDT]: 'An IDT string specifiying bases and modifications of an oligo.',
  // Note this description is usually replaced with a more specific description.
  [COLUMN_TYPE.RESIDUES]: 'The residues to set.',
  [COLUMN_TYPE.SEQUENCE_TOPOLOGY]: 'The topology (circular or linear) of the DNA sequence.',
  [COLUMN_TYPE.BATCH_NAME]: 'The batch ID used to identify the batch to be updated.',
  [COLUMN_TYPE.IGNORE]: 'This column will be ignored.',
  [COLUMN_TYPE.CUSTOM_FIELD]:
    'The value of a custom field. The column name will become the name of the field, and ' +
    "the cell value will become the field's value.",
  [COLUMN_TYPE.SCHEMA_FIELD]:
    `The value of a field that's part of the schema you selected earlier. ` +
    `If you pick a field that has a "Schema Link" type, we'll search for an ` +
    `entity with a matching name. You can specify which folders to search in ` +
    `below.`,
  [COLUMN_TYPE.CONTAINER_SCHEMA_FIELD]:
    "The value of a field that's part of the container schema you selected earlier.",
  [COLUMN_TYPE.PLATE_SCHEMA_FIELD]:
    "The value of a field that's part of the plate schema you selected earlier.",
  [COLUMN_TYPE.BOX_SCHEMA_FIELD]: `The value of a field that's part of the box schema you selected earlier.`,
  [COLUMN_TYPE.LOCATION_SCHEMA_FIELD]:
    "The value of a field that's part of the location schema you selected earlier.",
  [COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD]: 'An individual sample',
  [COLUMN_TYPE.RESULT_SCHEMA_FIELD]: 'The value of a result field',
  [COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD]: 'The value of a task field',
  [COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD]: 'The value of an output field',
  [COLUMN_TYPE.NUMBER_OF_CONTAINERS]: 'The number of containers to create.',
  [COLUMN_TYPE.STORABLE_NAME]: "The name to identify the inventory item. Defaults to item's barcode.",
  [COLUMN_TYPE.BARCODE]: 'The barcode for the item.',
  [COLUMN_TYPE.NEW_BARCODE]: 'The new barcode for the inventory item.',
  [COLUMN_TYPE.PARENT_STORABLE_BARCODE]: 'The barcode of the containing inventory.',
  [COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES]:
    'The position inside the containing inventory (if the Location refers to a plate or a box).',
  [COLUMN_TYPE.INPUT_CONTAINER_BARCODE]:
    'The barcode of the container that you are transferring material from.',
  [COLUMN_TYPE.INPUT_BIOENTITY_LABEL]:
    'The name, alias, or registry ID of the entity that you are transferring material from.',
  [COLUMN_TYPE.INPUT_BATCH_NAME]: 'The ID of the batch that you are transferring material from.',
  [COLUMN_TYPE.INPUT_GRID_BARCODE]:
    'The barcode of the plate that you are transferring material from. You ' +
    'must also specify the well in a separate column.',
  [COLUMN_TYPE.INPUT_GRID_COORDINATES]:
    'The well of the plate, such as "A6" or "6", that you are transferring material from.',
  [COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE]:
    'The barcode of the container that you are transferring material to.',
  [COLUMN_TYPE.OUTPUT_GRID_BARCODE]:
    'The barcode of the plate that you are transferring material to. You ' +
    'must also specify the well in a separate column.',
  [COLUMN_TYPE.OUTPUT_GRID_COORDINATES]:
    'The well of the plate, such as "A6" or "6", that you are transferring material to.',
  [COLUMN_TYPE.INPUT_CONCENTRATION_VALUE]: 'Concentration of the source sample.',
  [COLUMN_TYPE.CONCENTRATION_UNITS]:
    'Concentration units: molar (M) or mass concentration (g/L) with prefixes: n, u, m, or none.',
  [COLUMN_TYPE.FINAL_CONCENTRATION_VALUE]:
    'Concentration of the container. Only supported for single content containers.',
  [COLUMN_TYPE.TRANSFER_QUANTITY]: `The ${getQuantityDisplayString()} of the material that you are transferring.`,
  [COLUMN_TYPE.FINAL_QUANTITY_VALUE]:
    `Sum of the destination container's initial ` +
    `${getQuantityDisplayString()}, transfer ${getQuantityDisplayString()}, and dilution ${getQuantityDisplayString()}.`,
  [COLUMN_TYPE.SOURCE_FINAL_QUANTITY_VALUE]:
    `Sum of the destination container's source's initial ` +
    `${getQuantityDisplayString()}, transfer ${getQuantityDisplayString()}, and dilution ${getQuantityDisplayString()}.`,
  [COLUMN_TYPE.QUANTITY_UNITS]: `${getQuantityDisplayString(true)} units: pL, nL, uL, mL, L${
    BenchlingConfig.INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER ? ', pg, ng, ug, mg, g, kg' : ''
  }`,
  [COLUMN_TYPE.ADD_DEDUCT_AMOUNT]: `The ${getQuantityDisplayString(
    false
  )} of material that you are adding or deducting.`,
  [COLUMN_TYPE.EXPIRATION_DATE_OVERRIDE]:
    'Expiration date override for the container. Only applicable for containers that ' +
    'contain entities with expiration tracking enabled.',
  [COLUMN_TYPE.FREEZE_THAW_COUNT]:
    'Freeze/thaw count for the container. Only applicable for containers that contain entities with ' +
    'freeze/thaw tracking enabled.',
  [COLUMN_TYPE.ALLOWS_NON_LOCATION_CHILDREN]:
    'Whether the location can contain plates, boxes, and containers. When not enabled, the location can only contain other locations.',
  [COLUMN_TYPE.LOCATION_CAPACITY]: 'The number of plates, boxes, and containers that the location can hold.',
  [COLUMN_TYPE.ALLOWED_CHILD_STORABLE_SCHEMAS]:
    'The plate, box, and container schemas allowed to be stored in the location.',
  [COLUMN_TYPE.SAMPLE_OWNERS]: 'The names, handles, or emails of the sample owners for the item.',
  [COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS]:
    'The restriction status of the item ("Restricted" or "Unrestricted").',
  [COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS]: `The names, handles, or emails of the ${getRestrictedSampleUsersDisplayText()} for the item.`,
  [COLUMN_TYPE.STAGE_RUN_NAME]: 'The name of the stage run to be used.',
  [COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME]: 'The name of the sample to be used.',
  [COLUMN_TYPE.DATA_TABLE_MATCHER]: `The ${MODEL_NAMES.result_table} column on which rows will be matched.`,
  [COLUMN_TYPE.DATA_TABLE_VALUE]: `The ${MODEL_NAMES.result_table} column which will be modified`,
  [COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY]: 'The entity to create a batch for',
  // Mixture ingredients
  [COLUMN_TYPE.INGREDIENT_MIXTURE_LABEL]: 'The mixture that this ingredient belongs to.',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_BIOENTITY_LABEL]:
    'The component entity of this ingredient. ' +
    'Will create a new ingredient if none yet exists with this component entity.',
  [COLUMN_TYPE.INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL]:
    'Use this column type to replace the component entity of an existing ingredient. ' +
    'The row must also specify the current component entity in order to identify the ingredient.',
  [COLUMN_TYPE.INGREDIENT_CATALOG_IDENTIFIER]: 'Text value.',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_TEXT]: 'Text representation of the component lot.',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL]:
    'The Name, Alias, or Registry ID of the component lot entity.',
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE]: 'The barcode of the component lot container.',
  [COLUMN_TYPE.INGREDIENT_AMOUNT]: 'Positive number (eg "1.2", "5.2e-3")',
  [COLUMN_TYPE.INGREDIENT_UNITS]: `One of: ${ORDERED_INGREDIENT_UNITS_FOR_DISPLAY.join(', ')}.`,
  [COLUMN_TYPE.INGREDIENT_NOTES]: 'Any additional comments or notes about the ingredient.',
  // Processes
  [COLUMN_TYPE.PROCESS_TASK_ASSIGNEE]: 'The assignee of this task',
  [COLUMN_TYPE.PROCESS_TASK]: 'The task for this output',
  // Procedures
  [COLUMN_TYPE.PROCEDURE_CONDITION_REPLICATES]: 'The condition name and replicate number of an experiment',
  // Chemistry
  [COLUMN_TYPE.SMILES]: 'SMILES string describing chemical structure.',
  [COLUMN_TYPE.MOLFILE]: 'Molfile string describing chemical structure.',
  [COLUMN_TYPE.CUSTOM_MOD_SYNTAX]:
    'A customer-defined Custom Notation string specifying bases and modifications of an oligo.',
  [COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT]: 'A component entity of an oligo duplex or conjugate',
  [COLUMN_TYPE.COMPLEX_POLYMER_CONNECTION]:
    'All connections between component entities of an oligo duplex or conjugate',
  [COLUMN_TYPE.OLIGO_DUPLEX_OFFSET]: "The 5' offset for the forward strand",
  // Studies
  [COLUMN_TYPE.STUDY_LABELS]: 'The ID of the study that will be assigned to the created items.',
  // Trial subjects
  [COLUMN_TYPE.TRIAL_SUBJECT_NAME]: 'The name for the trial subject.',
  [COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD]: 'An individual trial subject.',
  [COLUMN_TYPE.TRIAL_SUBJECT_MY_COLLECTION_ID]:
    'A unique alphanumeric value to identify the trial subject within the collection.',
  [COLUMN_TYPE.TRIAL_SUBJECT_ORG_COLLECTION_ID]:
    'A unique alphanumeric value to identify the trial subject within the collection.',
};

export const COLUMN_TYPE_TO_SORT_ORDER: {[key in COLUMN_TYPE]?: number} = {
  // Top-level types
  [COLUMN_TYPE.FILENAME]: 0,
  [COLUMN_TYPE.STAGE_RUN_NAME]: 0,
  [COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME]: 0,
  [COLUMN_TYPE.MY_COLLECTION_ID]: 0,
  [COLUMN_TYPE.ORG_COLLECTION_ID]: 0,
  [COLUMN_TYPE.BATCH_NAME]: 0,
  [COLUMN_TYPE.RESIDUES]: 0,
  [COLUMN_TYPE.SEQUENCE_TOPOLOGY]: 0,
  [COLUMN_TYPE.SMILES]: 0,
  [COLUMN_TYPE.MOLFILE]: 0,
  [COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT]: 0,
  [COLUMN_TYPE.COMPLEX_POLYMER_CONNECTION]: 0,
  [COLUMN_TYPE.OLIGO_DUPLEX_OFFSET]: 0,
  [COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD]: 0,
  [COLUMN_TYPE.RESULT_SCHEMA_FIELD]: 0,
  [COLUMN_TYPE.INGREDIENT_MIXTURE_LABEL]: 0,
  // Processes
  [COLUMN_TYPE.PROCESS_TASK_ASSIGNEE]: 0,
  [COLUMN_TYPE.PROCESS_TASK]: 0,
  [COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD]: 0,
  [COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD]: 0,
  // Procedures
  [COLUMN_TYPE.PROCEDURE_CONDITION_REPLICATES]: 0,
  // Studies
  [COLUMN_TYPE.STUDY_LABELS]: 0,
  // Trial subject labels
  [COLUMN_TYPE.TRIAL_SUBJECT_NAME]: 0,
  [COLUMN_TYPE.TRIAL_SUBJECT_MY_COLLECTION_ID]: 0,
  [COLUMN_TYPE.TRIAL_SUBJECT_ORG_COLLECTION_ID]: 0,
  // Extra entity types
  [COLUMN_TYPE.HELM]: 1,
  [COLUMN_TYPE.IDT]: 1,
  [COLUMN_TYPE.ALIAS]: 1,
  [COLUMN_TYPE.DESCRIPTION]: 1,
  [COLUMN_TYPE.CUSTOM_FIELD]: 1,
  [COLUMN_TYPE.FOLDER_PATH_FORWARD_SLASH]: 1,
  // Entity and batch schema fields
  [COLUMN_TYPE.SCHEMA_FIELD]: 2,
  [COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD]: 2,
  // Storable types
  [COLUMN_TYPE.NUMBER_OF_CONTAINERS]: 3,
  [COLUMN_TYPE.STORABLE_NAME]: 3,
  [COLUMN_TYPE.BARCODE]: 3,
  [COLUMN_TYPE.NEW_BARCODE]: 3,
  [COLUMN_TYPE.PARENT_STORABLE_BARCODE]: 3,
  [COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES]: 3,
  // Storable schema fields
  [COLUMN_TYPE.CONTAINER_SCHEMA_FIELD]: 4,
  [COLUMN_TYPE.PLATE_SCHEMA_FIELD]: 4,
  [COLUMN_TYPE.BOX_SCHEMA_FIELD]: 4,
  [COLUMN_TYPE.LOCATION_SCHEMA_FIELD]: 4,
  // Transfer inputs
  [COLUMN_TYPE.INPUT_CONTAINER_BARCODE]: 5,
  [COLUMN_TYPE.INPUT_BIOENTITY_LABEL]: 5,
  [COLUMN_TYPE.INPUT_BATCH_NAME]: 5,
  [COLUMN_TYPE.INPUT_GRID_BARCODE]: 5,
  [COLUMN_TYPE.INPUT_GRID_COORDINATES]: 5,
  // Transfer outputs
  [COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE]: 6,
  [COLUMN_TYPE.OUTPUT_GRID_BARCODE]: 6,
  [COLUMN_TYPE.OUTPUT_GRID_COORDINATES]: 6,
  // Transfer measurements
  [COLUMN_TYPE.INPUT_CONCENTRATION_VALUE]: 7,
  [COLUMN_TYPE.CONCENTRATION_UNITS]: 7,
  [COLUMN_TYPE.FINAL_CONCENTRATION_VALUE]: 7,
  [COLUMN_TYPE.TRANSFER_QUANTITY]: 7,
  [COLUMN_TYPE.FINAL_QUANTITY_VALUE]: 7,
  [COLUMN_TYPE.SOURCE_FINAL_QUANTITY_VALUE]: 7,
  [COLUMN_TYPE.QUANTITY_UNITS]: 7,
  // Inventory measurements
  [COLUMN_TYPE.ADD_DEDUCT_AMOUNT]: 7,
  // Expiration & freeze/thaw
  [COLUMN_TYPE.EXPIRATION_DATE_OVERRIDE]: 7,
  // Location capacity configuration
  [COLUMN_TYPE.ALLOWS_NON_LOCATION_CHILDREN]: 7,
  [COLUMN_TYPE.LOCATION_CAPACITY]: 7,
  [COLUMN_TYPE.ALLOWED_CHILD_STORABLE_SCHEMAS]: 7,
  // Sample control roles
  [COLUMN_TYPE.SAMPLE_OWNERS]: 7,
  [COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS]: 7,
  [COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS]: 7,
  // Mixture ingredient import columns
  [COLUMN_TYPE.INGREDIENT_COMPONENT_BIOENTITY_LABEL]: 1,
  [COLUMN_TYPE.INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL]: 2,
  [COLUMN_TYPE.INGREDIENT_CATALOG_IDENTIFIER]: 3,
  [COLUMN_TYPE.INGREDIENT_AMOUNT]: 4,
  [COLUMN_TYPE.INGREDIENT_UNITS]: 5,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_TEXT]: 6,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE]: 7,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL]: 8,
  [COLUMN_TYPE.INGREDIENT_NOTES]: 9,
  // Always have ignore at the bottom
  [COLUMN_TYPE.IGNORE]: 100,
};

export const COLUMN_TYPES_ALLOWING_MULTIPLE_VALUES: Array<COLUMN_TYPE> = [
  COLUMN_TYPE.CUSTOM_FIELD,
  COLUMN_TYPE.DESCRIPTION,
  COLUMN_TYPE.ALIAS,
  COLUMN_TYPE.SCHEMA_FIELD,
  COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
  COLUMN_TYPE.PLATE_SCHEMA_FIELD,
  COLUMN_TYPE.BOX_SCHEMA_FIELD,
  COLUMN_TYPE.LOCATION_SCHEMA_FIELD,
  COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD,
  COLUMN_TYPE.RESULT_SCHEMA_FIELD,
  COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD,
  COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD,
  COLUMN_TYPE.IGNORE,
  COLUMN_TYPE.DATA_TABLE_MATCHER,
  COLUMN_TYPE.DATA_TABLE_VALUE,
  COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT,
  COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD,
];

// Keep in sync with benchling/bulk_adapters/derived_columns/derived_column_transform.py
export enum ColumnTransform {
  // These will be used as column names by the bulk adapter (not surfaced to user). Prefix with BNCH_ to
  // avoid collisions with user-defined columns.
  IDT_3MOD = 'BNCH_IDT_3MOD',
  IDT_5MOD = 'BNCH_IDT_5MOD',
  CHEMICAL_STRUCTURE_SALTS = 'BNCH_CHEMICAL_STRUCTURE_SALTS',
}

/**
 * Enum representing the source of a column mapping in bulk import operations.
 * Tracks how a column was mapped to determine appropriate UI behavior and validation.
 */
export enum ColumnMappingSource {
  /** Column mapping was automatically guessed by Benchling */
  BENCHLING_GUESSED = 'BENCHLING_GUESSED',
  /** Column mapping was guessed by Benchling and then confirmed by the user */
  BENCHLING_GUESS_CONFIRMED = 'BENCHLING_GUESS_CONFIRMED',
  /** Column mapping was manually selected by the user */
  USER_SELECTED = 'USER_SELECTED',
}

export interface DerivedColumnExampleValue {
  value: string;
  rowIdx: number;
  originalValues: {[key in COLUMN_TYPE]?: string};
}

const DERIVED_COLUMN_SHAPE = PropTypes.shape({
  columnTransform: PropTypes.oneOf(Object.values(ColumnTransform)).isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.keys(COLUMN_TYPE)).isRequired,
  allowedColumnTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(COLUMN_TYPE))).isRequired,
  exampleValues: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      rowIdx: PropTypes.number.isRequired,
      originalValues: PropTypes.object.isRequired,
    })
  ),
  numRowsWithDerivedValues: PropTypes.number.isRequired,
  compatibleSchemaFieldIds: PropTypes.arrayOf(PropTypes.string),
});

export interface DerivedColumn extends SpreadsheetColumn {
  columnTransform: ColumnTransform;
  // The allowed types for derived column, see
  // benchling.bulk_adapters.derived_columns.derived_column_transform
  allowedColumnTypes: Array<COLUMN_TYPE>;
  exampleValues: Array<DerivedColumnExampleValue>;
  // Number of rows in the derived column that contain a non-null derived value.
  numRowsWithDerivedValues: number;
  // The allowed schema field IDs for derived column, if one of the `columnTypes` is COLUMN_TYPE.SCHEMA_FIELD;
  // computed by looking at the values in the derived column and checking which schema fields would accept the
  // values; used to ensure that the user can only choose schema fields that will not cause validation errors
  // when mapping derived columns.
  compatibleSchemaFieldIds?: Array<string>;
}

export interface DerivedColumnsData {
  derivedColumns: Array<DerivedColumn> | null;
  state: TaskState | null;
}

export const DERIVED_COLUMNS_DATA_SHAPE = PropTypes.shape({
  derivedColumns: PropTypes.arrayOf(DERIVED_COLUMN_SHAPE),
  state: PropTypes.oneOf([TaskState.STARTED, TaskState.FAILED, TaskState.COMPLETED]),
});

export interface SampleDataProps {
  derivedValue: string;
  originalValues: {[key in COLUMN_TYPE]?: string};
  columnTransform: ColumnTransform;
}

export type DerivedColumnsMapping = {[key in ColumnTransform]?: COLUMN_TYPE};

export interface ColumnTransformConfiguration {
  sampleDataRenderer: (props: SampleDataProps) => JSX.Element;
  columnTransform: ColumnTransform;
  helpText?: string;
}

export const DerivedColumnsStoreKeys = {
  // Both create and update use the same key in the store because:
  // - The logic for finding/setting derived columns is identical in either case.
  // - Users can't run both create and update in the same browser at the same time.
  BULK_CREATE: 'derivedColumns' as const,
  BULK_UPDATE: 'derivedColumns' as const,
};

type DerivedColumnsStoreKeysUnion = (typeof DerivedColumnsStoreKeys)[keyof typeof DerivedColumnsStoreKeys];

export const DERIVED_COLUMNS_SUPPORTED_XFORM_CONFIGS: Array<ColumnTransformConfiguration> = [
  {columnTransform: ColumnTransform.IDT_3MOD, sampleDataRenderer: TerminalProbesSampleData},
  {columnTransform: ColumnTransform.IDT_5MOD, sampleDataRenderer: TerminalProbesSampleData},
  {
    columnTransform: ColumnTransform.CHEMICAL_STRUCTURE_SALTS,
    sampleDataRenderer: ChemicalStructureSaltsSampleData,
    helpText:
      'We are currently optimized to support capturing parent molecule compounds using our Molecule entity. In concordance with that, any associated salt form information will be stripped; we recommend capturing this in custom or schema fields.',
  },
];

// Exported to avoid TS warning that the value is never read.
// If this fails typechecking, it means DerivedColumnsStoreKeys is missing a type! Please fill it in!
export const __checkTypes: Omit<DerivedColumnsData, DerivedColumnsStoreKeysUnion | 'state'> = {};

export enum IMPORT_TYPE {
  BOXES = 'BOXES',
  TRIAL_SAMPLES = 'TRIAL_SAMPLES',
  CONTAINERS = 'CONTAINERS',
  DATA_TABLE = 'DATA_TABLE',
  EXAMPLE_FILE = 'EXAMPLE_FILE',
  INPUT_TABLE = 'INPUT_TABLE',
  INVENTORY_FILES = 'INVENTORY_FILES',
  LOCATIONS = 'LOCATIONS',
  PLATES = 'PLATES',
  REGISTRATION_TABLE = 'REGISTRATION_TABLE',
  REGISTRY_ENTITIES = 'REGISTRY_ENTITIES',
  REQUEST_SAMPLES = 'REQUEST_SAMPLES',
  RUN_RESULTS = 'RUN_RESULTS',
  SAMPLES = 'SAMPLES',
  STRUCTURED_TABLE = 'STRUCTURED_TABLE',
  TRANSFER_STORABLES = 'TRANSFER_STORABLES',
  TRANSFER_TABLE = 'TRANSFER_TABLE',
  UPDATE_BIOENTITIES = 'UPDATE_BIOENTITIES',
  UPDATE_BOXES = 'UPDATE_BOXES',
  UPDATE_CONTAINERS = 'UPDATE_CONTAINERS',
  UPDATE_MIXTURE_INGREDIENTS = 'UPDATE_MIXTURE_INGREDIENTS',
  UPDATE_LOCATIONS = 'UPDATE_LOCATIONS',
  UPDATE_PLATES = 'UPDATE_PLATES',
  UPDATE_SAMPLES = 'UPDATE_SAMPLES',
}

export function getAnalyticsModuleFromImportType(importType?: IMPORT_TYPE): ANALYTICS_MODULE | null {
  if (importType == null) {
    return null;
  }
  switch (importType) {
    // Inventory imports
    case IMPORT_TYPE.BOXES:
    case IMPORT_TYPE.CONTAINERS:
    case IMPORT_TYPE.LOCATIONS:
    case IMPORT_TYPE.PLATES:
    case IMPORT_TYPE.TRANSFER_STORABLES:
    case IMPORT_TYPE.TRANSFER_TABLE:
    case IMPORT_TYPE.UPDATE_BOXES:
    case IMPORT_TYPE.UPDATE_CONTAINERS:
    case IMPORT_TYPE.UPDATE_PLATES:
    case IMPORT_TYPE.UPDATE_LOCATIONS:
      return ANALYTICS_MODULE.SAMPLES;
    // Registry imports
    case IMPORT_TYPE.TRIAL_SAMPLES:
    case IMPORT_TYPE.INVENTORY_FILES:
    case IMPORT_TYPE.REGISTRATION_TABLE:
    case IMPORT_TYPE.REGISTRY_ENTITIES:
    case IMPORT_TYPE.UPDATE_BIOENTITIES:
    case IMPORT_TYPE.UPDATE_MIXTURE_INGREDIENTS:
    case IMPORT_TYPE.SAMPLES: // import batches
    case IMPORT_TYPE.UPDATE_SAMPLES: // update batches
      return ANALYTICS_MODULE.REGISTRY;
    // Miscellaneous other imports
    case IMPORT_TYPE.EXAMPLE_FILE:
      return ANALYTICS_MODULE.INSTRUMENTS;
    case IMPORT_TYPE.INPUT_TABLE:
      return ANALYTICS_MODULE.LEGACY_WORKFLOWS;
    case IMPORT_TYPE.REQUEST_SAMPLES:
      return ANALYTICS_MODULE.REQUESTS;
    case IMPORT_TYPE.DATA_TABLE:
    case IMPORT_TYPE.RUN_RESULTS:
      return ANALYTICS_MODULE.RESULTS;
    case IMPORT_TYPE.STRUCTURED_TABLE:
      return ANALYTICS_MODULE.ELN;
    default:
      assertAndContinue(false, `Unexpected spreadsheet import type ${assertNever(importType)}`);
      return null;
  }
}

export enum ImportSourceType {
  SPREADSHEET = 'SPREADSHEET',
  RAW_TEXT = 'RAW_TEXT',
  SDF = 'SDF',
}

export const DEFAULT_IMPORT_SOURCE_TYPE = ImportSourceType.SPREADSHEET;

export const RAW_TEXT_SPREADSHEET_NAME = 'bulk_import_raw_text.csv';

// The column types to choose from when mapping spreadsheet columns to file attributes.
// The order of the column types here is the order the columns are going to appear in
// the Column Type dropdown menu.
export const VALUE_COLUMN_TYPES = [
  COLUMN_TYPE.ALIAS,
  COLUMN_TYPE.SCHEMA_FIELD,
  COLUMN_TYPE.CUSTOM_FIELD,
  COLUMN_TYPE.DESCRIPTION,
  COLUMN_TYPE.IGNORE,
];

export const SCHEMA_FIELD_COLUMN_TYPES = [
  COLUMN_TYPE.SCHEMA_FIELD,
  COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
  COLUMN_TYPE.PLATE_SCHEMA_FIELD,
  COLUMN_TYPE.BOX_SCHEMA_FIELD,
  COLUMN_TYPE.LOCATION_SCHEMA_FIELD,
  COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD,
  COLUMN_TYPE.RESULT_SCHEMA_FIELD,
  COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD,
  COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD,
  COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD,
];

export const STORABLE_BASE_COLUMN_TYPES = [
  COLUMN_TYPE.BARCODE,
  COLUMN_TYPE.STORABLE_NAME,
  COLUMN_TYPE.PARENT_STORABLE_BARCODE,
];

export const STORABLE_BARCODE_COLUMN_TYPES = [COLUMN_TYPE.BARCODE, COLUMN_TYPE.NEW_BARCODE];

export const STORABLE_COLUMN_TYPES = [
  ...STORABLE_BASE_COLUMN_TYPES,
  COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES,
];

export const PARENT_STORABLE_COLUMN_TYPES = [
  COLUMN_TYPE.PARENT_STORABLE_BARCODE,
  COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES,
];

export const TRANSFER_PRIMARY_STORABLE_INPUT_COLUMN_TYPES = [
  COLUMN_TYPE.INPUT_CONTAINER_BARCODE,
  COLUMN_TYPE.INPUT_GRID_BARCODE,
];
export const TRANSFER_PRIMARY_INPUT_COLUMN_TYPES = [
  COLUMN_TYPE.INPUT_BIOENTITY_LABEL,
  COLUMN_TYPE.INPUT_BATCH_NAME,
  ...TRANSFER_PRIMARY_STORABLE_INPUT_COLUMN_TYPES,
];
export const TRANSFER_INPUT_COLUMN_TYPES = [
  ...TRANSFER_PRIMARY_INPUT_COLUMN_TYPES,
  COLUMN_TYPE.INPUT_GRID_COORDINATES,
];
export const TRANSFER_PRIMARY_OUTPUT_COLUMN_TYPES = [
  COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE,
  COLUMN_TYPE.OUTPUT_GRID_BARCODE,
];
export const TRANSFER_OUTPUT_COLUMN_TYPES = [
  ...TRANSFER_PRIMARY_OUTPUT_COLUMN_TYPES,
  COLUMN_TYPE.OUTPUT_GRID_COORDINATES,
];
export const TRANSFER_QUANTITY_COLUMN_TYPES = [COLUMN_TYPE.TRANSFER_QUANTITY, COLUMN_TYPE.QUANTITY_UNITS];
export const FINAL_QUANTITY_COLUMN_TYPES = [COLUMN_TYPE.FINAL_QUANTITY_VALUE, COLUMN_TYPE.QUANTITY_UNITS];
export const SOURCE_FINAL_QUANTITY_COLUMN_TYPES = [
  COLUMN_TYPE.SOURCE_FINAL_QUANTITY_VALUE,
  COLUMN_TYPE.QUANTITY_UNITS,
];
export const INPUT_CONCENTRATION_COLUMN_TYPES = [
  COLUMN_TYPE.INPUT_CONCENTRATION_VALUE,
  COLUMN_TYPE.CONCENTRATION_UNITS,
];
export const FINAL_CONCENTRATION_COLUMN_TYPES = [
  COLUMN_TYPE.FINAL_CONCENTRATION_VALUE,
  COLUMN_TYPE.CONCENTRATION_UNITS,
];
export const GRID_BARCODE_TO_COORDINATES_COLUMN_TYPES = {
  [COLUMN_TYPE.INPUT_GRID_BARCODE]: COLUMN_TYPE.INPUT_GRID_COORDINATES,
  [COLUMN_TYPE.OUTPUT_GRID_BARCODE]: COLUMN_TYPE.OUTPUT_GRID_COORDINATES,
};

export const TRANSFER_MEASUREMENT_TYPES = [
  COLUMN_TYPE.TRANSFER_QUANTITY,
  COLUMN_TYPE.FINAL_QUANTITY_VALUE,
  COLUMN_TYPE.SOURCE_FINAL_QUANTITY_VALUE,
  COLUMN_TYPE.QUANTITY_UNITS,
  COLUMN_TYPE.INPUT_CONCENTRATION_VALUE,
  COLUMN_TYPE.CONCENTRATION_UNITS,
];

export const TRANSFER_COLUMN_TYPES = [
  ...TRANSFER_INPUT_COLUMN_TYPES,
  ...TRANSFER_OUTPUT_COLUMN_TYPES,
  ...TRANSFER_MEASUREMENT_TYPES,
];

export const EXTRA_FILE_COLUMN_TYPES = [COLUMN_TYPE.ALIAS, COLUMN_TYPE.DESCRIPTION, COLUMN_TYPE.CUSTOM_FIELD];

export const INGREDIENT_COMPONENT_BIOENTITY_COLUMN_TYPES = [
  COLUMN_TYPE.INGREDIENT_COMPONENT_BIOENTITY_LABEL,
  COLUMN_TYPE.INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL,
];

export const INGREDIENT_COMPONENT_LOT_COLUMN_TYPES = [
  COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_TEXT,
  COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE,
  COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL,
];

export const UPDATE_MIXTURE_INGREDIENT_COLUMN_TYPES = [
  ...INGREDIENT_COMPONENT_BIOENTITY_COLUMN_TYPES,
  COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_TEXT,
  COLUMN_TYPE.INGREDIENT_MIXTURE_LABEL,
  COLUMN_TYPE.INGREDIENT_CATALOG_IDENTIFIER,
  COLUMN_TYPE.INGREDIENT_AMOUNT,
  COLUMN_TYPE.INGREDIENT_UNITS,
  COLUMN_TYPE.INGREDIENT_NOTES,
  COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE,
  COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL,
];

export const PROCESS_COLUMN_TYPES = [COLUMN_TYPE.PROCESS_TASK_ASSIGNEE, COLUMN_TYPE.PROCESS_TASK];

export const MOLECULE_COLUMN_TYPES = [COLUMN_TYPE.SMILES, COLUMN_TYPE.MOLFILE];

export const COMPLEX_POLYMER_COLUMN_TYPES = [
  COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT,
  COLUMN_TYPE.COMPLEX_POLYMER_CONNECTION,
];

export const COLUMN_TYPES_PRIORITIZED_OVER_SCHEMA_FIELDS = [...MOLECULE_COLUMN_TYPES];

export const COLUMN_TYPES_TO_CHECK_FOR_DERIVED_COLUMNS = [
  COLUMN_TYPE.IDT,
  COLUMN_TYPE.CUSTOM_MOD_SYNTAX,
  ...MOLECULE_COLUMN_TYPES,
];

export const TRIAL_SUBJECT_COLUMN_TYPES = [
  COLUMN_TYPE.TRIAL_SUBJECT_NAME,
  COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD,
  COLUMN_TYPE.TRIAL_SUBJECT_MY_COLLECTION_ID,
  COLUMN_TYPE.TRIAL_SUBJECT_ORG_COLLECTION_ID,
];

export const ALL_COLUMN_TYPES = [
  COLUMN_TYPE.FILENAME,
  COLUMN_TYPE.STAGE_RUN_NAME,
  COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME,
  COLUMN_TYPE.NUMBER_OF_CONTAINERS,
  COLUMN_TYPE.MY_COLLECTION_ID,
  COLUMN_TYPE.ORG_COLLECTION_ID,
  COLUMN_TYPE.BATCH_NAME,
  COLUMN_TYPE.HELM,
  COLUMN_TYPE.IDT,
  COLUMN_TYPE.CUSTOM_MOD_SYNTAX,
  COLUMN_TYPE.RESIDUES,
  COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
  COLUMN_TYPE.PLATE_SCHEMA_FIELD,
  COLUMN_TYPE.BOX_SCHEMA_FIELD,
  COLUMN_TYPE.LOCATION_SCHEMA_FIELD,
  COLUMN_TYPE.DATA_TABLE_MATCHER,
  COLUMN_TYPE.DATA_TABLE_VALUE,
  COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD,
  COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY,
  COLUMN_TYPE.RESULT_SCHEMA_FIELD,
  COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD,
  COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD,
  COLUMN_TYPE.FOLDER_PATH_FORWARD_SLASH,
  COLUMN_TYPE.ADD_DEDUCT_AMOUNT,
  COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS,
  COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS,
  COLUMN_TYPE.SAMPLE_OWNERS,
  COLUMN_TYPE.NEW_BARCODE,
  COLUMN_TYPE.STUDY_LABELS,
  ...STORABLE_COLUMN_TYPES,
  ...TRANSFER_COLUMN_TYPES,
  ...VALUE_COLUMN_TYPES,
  ...UPDATE_MIXTURE_INGREDIENT_COLUMN_TYPES,
  ...PROCESS_COLUMN_TYPES,
  ...MOLECULE_COLUMN_TYPES,
  ...COMPLEX_POLYMER_COLUMN_TYPES,
  ...TRIAL_SUBJECT_COLUMN_TYPES,
];

export enum OPTIONS {
  DELETE_EMPTY_FIELDS = 'deleteEmptyFields',
  DELETE_EXISTING_ALIASES = 'deleteExistingAliases',
  CLEAR_INGREDIENT_FIELDS_FOR_EMPTY_CELLS = 'clearIngredientFieldsForEmptyCells',
  CLEAR_SAMPLE_ROLES_FOR_EMPTY_CELLS = 'clearSampleRolesForEmptyCells',
  IMPORT_CONTAINERS = 'shouldImportContainers',
  ARE_SEQUENCES_CIRCULAR = 'areSequencesCircular',
  REPLACE_LINK_WITH_TEXT = 'replaceFileLinkWithText',
  USE_EXISTING_CONTAINERS = 'useExistingContainers',
  USE_NON_EMPTY_CONTAINERS = 'useNonEmptyContainers',
  SKIP_ERRORS = 'shouldSkipErrors',
  USE_SAMPLES_WITH_STATUS_WARNINGS = 'useSamplesWithStatusWarnings',
}

export const OPTIONS_SAVED_AS_PREFERENCE = [OPTIONS.ARE_SEQUENCES_CIRCULAR];

export enum PaneKey {
  SET_STORABLE_IMPORT_SETTINGS = 'SET_STORABLE_IMPORT_SETTINGS',
  SET_IMPORT_SETTINGS_FOR_DESTINATION = 'SET_IMPORT_SETTINGS_FOR_DESTINATION',
  SET_IMPORT_SETTINGS_FOR_REGISTRY = 'SET_IMPORT_SETTINGS_FOR_REGISTRY',
  UPLOAD_SPREADSHEET = 'UPLOAD_SPREADSHEET',
  MAP_COLUMN_NAMES = 'MAP_COLUMN_NAMES',
  MAP_DERIVED_COLUMNS = 'MAP_DERIVED_COLUMNS',
  MAP_OUTPUT_FILE_COLUMNS = 'MAP_OUTPUT_FILE_COLUMNS',
  SHOW_ERRORS = 'SHOW_ERRORS',
  CONNECTION_FILES = 'CONNECTION_FILES',
  DATASET_CONFIG = 'DATASET_CONFIG',
  SET_TRIAL_SAMPLES_IMPORT_SETTINGS = 'SET_TRIAL_SAMPLES_IMPORT_SETTINGS',
}

export type Pane =
  | ChooseImportLocationPane
  | ChooseImportIntoRegistryPane
  | StorableImportSettingsPane
  | UploadSpreadsheetPane
  | MapColumnsPane
  | MapDerivedColumnsPane
  | MapOutputFileColumnsPane
  | ShowErrorsPane
  | ImportConnectionFilesPane
  | DatasetConfigPane
  | ChooseImportTrialSamplesPane;

export type PaneConfiguration =
  | ChooseImportLocationModalPaneConfiguration
  | StorableImportSettingsModalPaneConfiguration
  | UploadSpreadsheetModalPaneConfiguration
  | MapColumnNamesModalPaneConfig
  | MapDerivedColumnsModalPaneConfiguration
  | SetColumnsMapModalConfiguration
  | DatasetConfigModalConfiguration
  | ShowErrorsModalPaneConfiguration
  | ImportConnectionFilesModalConfiguration;

export interface ChooseImportLocationPane {
  paneKey: PaneKey.SET_IMPORT_SETTINGS_FOR_DESTINATION;
  configuration: ChooseImportLocationModalPaneConfiguration;
}

export interface ChooseImportIntoRegistryPane {
  paneKey: PaneKey.SET_IMPORT_SETTINGS_FOR_REGISTRY;
  configuration: null;
}

export interface ChooseImportTrialSamplesPane {
  paneKey: PaneKey.SET_TRIAL_SAMPLES_IMPORT_SETTINGS;
  configuration: null;
}

export interface StorableImportSettingsPane {
  paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS;
  configuration: StorableImportSettingsModalPaneConfiguration;
}

export interface UploadSpreadsheetPane {
  paneKey: PaneKey.UPLOAD_SPREADSHEET;
  configuration: UploadSpreadsheetModalPaneConfiguration;
}

export interface MapColumnsPane {
  paneKey: PaneKey.MAP_COLUMN_NAMES;
  configuration: MapColumnNamesModalPaneConfig;
}

export interface MapDerivedColumnsPane {
  paneKey: PaneKey.MAP_DERIVED_COLUMNS;
  configuration: MapDerivedColumnsModalPaneConfiguration;
}

export interface ShowErrorsPane {
  paneKey: PaneKey.SHOW_ERRORS;
  configuration: ShowErrorsModalPaneConfiguration;
}

export interface MapOutputFileColumnsPane {
  paneKey: PaneKey.MAP_OUTPUT_FILE_COLUMNS;
  configuration: SetColumnsMapModalConfiguration;
}

interface ImportConnectionFilesModalConfiguration {
  nextPaneKey: PaneKey;
  adapterId?: string;
  delimiter: string;
  automationOutputFileConfig: ClientAutomationOutputFileConfigSpec;
}

export interface ImportConnectionFilesPane {
  paneKey: PaneKey.CONNECTION_FILES;
  configuration: ImportConnectionFilesModalConfiguration;
}

export interface DatasetConfigPane {
  paneKey: PaneKey.DATASET_CONFIG;
  configuration: DatasetConfigModalConfiguration;
}

export interface GetValidColumnTypesArgs {
  registryImportSettings: RegistryImportSettingsProps;
  selectedSchemaIds: SelectedSchemaIds;
  options: BulkImportOptions;
  dataTableKeyFields?: Array<COLUMN_TYPE> | undefined;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes | null;
}

export type GetValidColumnTypesFunction = (args: GetValidColumnTypesArgs) => Array<COLUMN_TYPE>;

export type GetSchemaFieldsToHideByColumnFunction = () => Map<COLUMN_TYPE, Array<string>>;

export const SPREADSHEET_MIME_TYPE = [
  '.csv',
  '.txt',
  '.tsv',
  '.xlsx',
  '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
].join(', ');

export const SDF_MIME_TYPE = '.sdf, .txt';

export const SPREADSHEET_BULK_IMPORT_KEY = 'SPREADSHEET_BULK_IMPORT_KEY';

const ERRORS_SHAPE = PropTypes.arrayOf(
  PropTypes.shape({
    row: PropTypes.number.isRequired,
    errorCode: PropTypes.string.isRequired,
    errorMessage: PropTypes.string,
    itemName: PropTypes.string,
  }).isRequired
);

interface LabelingStrategyProps {
  labelingStrategy: LabelingStrategy | null;
  labelingStrategyOptions: Array<LabelingStrategy>;
}

interface TrialLabelingStrategyProps {
  trialSubjectLabelingStrategy?: LabelingStrategy | null;
  trialSubjectLabelingStrategyOptions?: Array<LabelingStrategy>;
}

export interface RegistryImportSettingsProps extends LabelingStrategyProps, TrialLabelingStrategyProps {
  registry: Registry | null;
  inOrgCollection: boolean;
  inUserCollection: boolean;
  // Project to associate with the imported registry item
  projectId?: string | null;
}

export interface BulkImportError {
  row: number;
  errorCode: string;
  errorMessage?: string;
  itemName?: string;
}

export interface StudyWarningBulkImportError extends BulkImportError {
  sampleHash: string;
}

export const REGISTRY_IMPORT_SETTINGS_PROPS = {
  registry: REGISTRY_SHAPE,
  inOrgCollection: PropTypes.bool.isRequired,
  inUserCollection: PropTypes.bool.isRequired,
  labelingStrategy: PropTypes.oneOf(SPREADSHEET_LABELING_STRATEGIES),
  labelingStrategyOptions: PropTypes.arrayOf(PropTypes.oneOf(SPREADSHEET_LABELING_STRATEGIES)).isRequired,
  trialSubjectLabelingStrategy: PropTypes.oneOf(SPREADSHEET_LABELING_STRATEGIES),
  trialSubjectLabelingStrategyOptions: PropTypes.arrayOf(PropTypes.oneOf(SPREADSHEET_LABELING_STRATEGIES)),
  // Project to associate with the imported registry item
  projectId: PropTypes.string,
};

export interface SpreadsheetColumn {
  tableColumnName?: string;
  type: COLUMN_TYPE;
  name: string;
  schemaField?: SchemaFieldConstants.SchemaField;
  sampleType?: keyof typeof SAMPLE_TYPE_DISPLAY;
  customSyntaxId?: string;
  complexPolymerComponent?: {name: string; schemaFieldId: string};
  dataType?: string;
  // TODO(BNCH-128910): Make this required
  columnMappingSource?: ColumnMappingSource;
}

export interface FileData {
  columns: Array<SpreadsheetColumn>;
  errorMessage?: string;
  state: TaskState;
  url?: string;
}

export const SpreadsheetPropTypes = {
  columnTypes: PropTypes.arrayOf(PropTypes.oneOf(ALL_COLUMN_TYPES).isRequired),
  errors: ERRORS_SHAPE,
  // Shape of errors returned from the server after validating spreadsheet.
  errorData: PropTypes.shape({
    itemNameErrors: ERRORS_SHAPE,
    registryErrors: ERRORS_SHAPE,
    registryWarnings: ERRORS_SHAPE,
    otherErrors: ERRORS_SHAPE,
    otherWarnings: ERRORS_SHAPE,
    tagErrors: ERRORS_SHAPE,
    state: PropTypes.oneOf([TaskState.STARTED, TaskState.FAILED, TaskState.COMPLETED]),
  }),
  column: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.oneOf(ALL_COLUMN_TYPES).isRequired,
    schemaField: SchemaFieldConstants.SCHEMA_FIELD_SHAPE,
  }),
  derivedColumnsData: DERIVED_COLUMNS_DATA_SHAPE,
  fileData: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        type: PropTypes.oneOf(Object.keys(COLUMN_TYPE)).isRequired,
      })
    ),
    errorMessage: PropTypes.string,
    state: PropTypes.oneOf([TaskState.STARTED, TaskState.FAILED, TaskState.COMPLETED]),
    url: PropTypes.string,
  }),
  modalProps: PropTypes.shape({
    title: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func.isRequired,
  }).isRequired,
  registryImportSettings: PropTypes.shape(REGISTRY_IMPORT_SETTINGS_PROPS),
  selectedDestination: PropTypes.shape({
    folderId: PropTypes.string,
    registryId: PropTypes.string,
  }),
  selectedSchemaIds: PropTypes.shape({
    tagSchemaId: PropTypes.string,
    batchSchemaId: PropTypes.string,
    containerSchemaId: PropTypes.string,
    plateSchemaId: PropTypes.string,
    boxSchemaId: PropTypes.string,
    locationSchemaId: PropTypes.string,
    resultSchemaId: PropTypes.string,
    processTaskSchemaId: PropTypes.string,
    processOutputSchemaId: PropTypes.string,
    trialSubjectSchemaId: PropTypes.string,
  }),
};

export interface SelectedSchemaIds {
  tagSchemaId?: string | null;
  batchSchemaId?: string | null;
  containerSchemaId?: string | null;
  plateSchemaId?: string | null;
  boxSchemaId?: string | null;
  locationSchemaId?: string | null;
  sampleGroupSchemaId?: string | null;
  resultSchemaId?: string | null;
  processTaskSchemaId?: string | null;
  processOutputSchemaId?: string | null;
  trialSubjectSchemaId?: string | null;
}

// Keep in sync with benchling/api/lib/bulk_spreadsheet.py:RowErrorCodes
export enum RowErrorCode {
  INVALID_AUTH = 'INVALID_AUTH',

  // File/entity errors
  NO_FILES_FOUND = 'NO_FILES_FOUND',
  MULTIPLE_FILES_FOUND = 'MULTIPLE_FILES_FOUND',
  NO_ENTRIES_FOUND = 'NO_ENTRIES_FOUND',
  MULTIPLE_ENTRIES_FOUND = 'MULTIPLE_ENTRIES_FOUND',
  NO_FILENAME = 'NO_FILENAME',
  FILENAME_TOO_LONG = 'FILENAME_TOO_LONG',
  READABLE_UNMODIFIABLE_BIOENTITY = 'READABLE_UNMODIFIABLE_BIOENTITY',
  ARCHIVED_UNMODIFIABLE_BIOENTITY = 'ARCHIVED_UNMODIFIABLE_BIOENTITY',
  UNREADABLE_BIOENTITY = 'UNREADABLE_BIOENTITY',
  ITEM_NOT_CONTAINABLE = 'ITEM_NOT_CONTAINABLE',
  INVALID_SEQUENCE_REGION = 'INVALID_SEQUENCE_REGION',
  RESIDUES_TOO_LONG = 'RESIDUES_TOO_LONG',

  // Batch errors
  MULTIPLE_BATCHES_FOUND = 'MULTIPLE_BATCHES_FOUND',
  NO_BATCHES_FOUND = 'NO_BATCHES_FOUND',
  NO_BATCH_NAME = 'NO_BATCH_NAME',
  READABLE_UNMODIFIABLE_BATCHES = 'READABLE_UNMODIFIABLE_BATCHES',
  ARCHIVED_UNMODIFIABLE_BATCHES = 'ARCHIVED_UNMODIFIABLE_BATCHES',
  UNREADABLE_BATCHES = 'UNREADABLE_BATCHES',

  // Run errors
  NO_RUNS_FOUND = 'NO_RUNS_FOUND',
  MULTIPLE_RUNS_FOUND = 'MULTIPLE_RUNS_FOUND',

  // Result errors
  NO_RESULTS_FOUND = 'NO_RESULTS_FOUND',
  MULTIPLE_RESULTS_FOUND = 'MULTIPLE_RESULTS_FOUND',

  // Request errors
  NO_REQUESTS_FOUND = 'NO_REQUESTS_FOUND',
  MULTIPLE_REQUESTS_FOUND = 'MULTIPLE_REQUESTS_FOUND',

  // Blob errors
  NO_BLOBS_FOUND = 'NO_BLOBS_FOUND',
  MULTIPLE_BLOBS_FOUND = 'MULTIPLE_BLOBS_FOUND',

  // Storable errors
  DUPLICATE_BARCODE = 'DUPLICATE_BARCODE',
  DUPLICATE_BARCODE_IN_SPREADSHEET = 'DUPLICATE_BARCODE_IN_SPREADSHEET',
  DUPLICATE_PARENT_STORABLE_IN_SPREADSHEET = 'DUPLICATE_PARENT_STORABLE_IN_SPREADSHEET',
  INVALID_GRID_COORDINATES = 'INVALID_GRID_COORDINATES',
  INVALID_NUMBER_OF_CONTAINERS = 'INVALID_NUMBER_OF_CONTAINERS',
  INVALID_PARENT_STORABLE = 'INVALID_PARENT_STORABLE',
  INVALID_SPECIFICATION = 'INVALID_SPECIFICATION',
  INVALID_STORABLE_WARNING = 'INVALID_STORABLE_WARNING',
  MULTIPLE_STORABLES_FOUND = 'MULTIPLE_STORABLES_FOUND',
  NO_STORABLE_NAME = 'NO_STORABLE_NAME',
  NO_STORABLES_FOUND = 'NO_STORABLES_FOUND',
  READABLE_UNMODIFIABLE_STORABLE = 'READABLE_UNMODIFIABLE_STORABLE',
  ARCHIVED_UNMODIFIABLE_STORABLE = 'ARCHIVED_UNMODIFIABLE_STORABLE',
  UNREADABLE_STORABLE = 'UNREADABLE_STORABLE',
  TOO_MANY_CONTAINERS_TO_MOVE = 'TOO_MANY_CONTAINERS_TO_MOVE',
  UNIT_ID_NOT_FOUND = 'UNIT_ID_NOT_FOUND',

  // Storable transfer errors
  INVALID_RESIDUES = 'INVALID_RESIDUES',
  INVALID_AMOUNT = 'INVALID_AMOUNT',
  INVALID_UNITS = 'INVALID_UNITS',
  INVALID_FINAL_QUANTITY = 'INVALID_FINAL_VOLUME',

  // Container errors
  FOUND_EXISTING_CONTAINER = 'FOUND_EXISTING_CONTAINER',
  IGNORED_COLUMN = 'IGNORED_COLUMN',
  NON_EMPTY_CONTAINER = 'NON_EMPTY_CONTAINER',
  NOT_A_CONTAINER = 'NOT_A_CONTAINER',
  EMPTY_CONTAINER = 'EMPTY_CONTAINER',
  MULTIPLE_BATCHES_IN_CONTAINER = 'MULTIPLE_BATCHES_IN_CONTAINER',
  MULTIPLE_SAMPLES_IN_CONTAINER = 'MULTIPLE_SAMPLES_IN_CONTAINER',
  PROJECT_REQUIRED_FOR_CONTAINERS = 'PROJECT_REQUIRED_FOR_CONTAINERS',
  FOUND_SAMPLE_WITH_BLOCKING_STATUS_WARNING = 'FOUND_SAMPLE_WITH_BLOCKING_STATUS_WARNING',
  FOUND_SAMPLE_WITH_NONBLOCKING_STATUS_WARNING = 'FOUND_SAMPLE_WITH_NONBLOCKING_STATUS_WARNING',
  // TODO(BNCH-57841): Drop deprecated value support when most clients have the code that supports NONBLOCKING
  DEPRECATED_FOUND_SAMPLE_WITH_STATUS_WARNING = 'FOUND_SAMPLE_WITH_STATUS_WARNING',

  // Tag errors
  INVALID_INTEGER = 'INVALID_INTEGER',
  INVALID_FLOAT = 'INVALID_FLOAT',
  INVALID_DATE = 'INVALID_DATE',
  INVALID_DATETIME = 'INVALID_DATETIME',
  INVALID_BOOLEAN = 'INVALID_BOOLEAN',
  INVALID_TAG_NAME = 'INVALID_TAG_NAME',
  INVALID_TAG_VALUE_LENGTH = 'INVALID_TAG_VALUE_LENGTH',
  INVALID_SELECTOR_OPTIONS = 'INVALID_SELECTOR_OPTIONS',

  // General bulk import errors
  MULTIPLE_LABELS_FOR_FILE = 'MULTIPLE_LABELS_FOR_FILE',
  DUPLICATE_ITEM_NAME = 'DUPLICATE_ITEM_NAME',
  DUPLICATE_ALIAS = 'DUPLICATE_ALIAS',
  REGISTRY_ERROR = 'REGISTRY_ERROR',
  SCHEMA_TYPE_MISMATCH = 'SCHEMA_TYPE_MISMATCH',
  SCHEMA_CHANGE_ERROR = 'SCHEMA_CHANGE_ERROR',
  SCHEMA_CHANGE_WARNING = 'SCHEMA_CHANGE_WARNING',

  // Stage run errors
  NO_STAGE_RUN_NAME = 'NO_STAGE_RUN_NAME',
  NO_STAGE_RUN_FOUND = 'NO_STAGE_RUN_FOUND',

  // Data table errors
  DATA_TABLE_ROW_MATCHED_MULTIPLE_TIMES = 'DATA_TABLE_ROW_MATCHED_MULTIPLE_TIMES',
  NO_DATA_TABLE_ROW_FOUND = 'NO_DATA_TABLE_ROW_FOUND',
  MULTIPLE_DATA_TABLE_ROWS_FOUND = 'MULTIPLE_DATA_TABLE_ROWS_FOUND',
  NO_STAGE_RUN_SAMPLE_FOUND = 'NO_STAGE_RUN_SAMPLE_FOUND',
  MULTIPLE_STAGE_RUN_SAMPLES_FOUND = 'MULTIPLE_STAGE_RUN_SAMPLES_FOUND',

  // Structured table errors
  INVALID_STRUCTURED_TABLE_TAG_VALUE = 'INVALID_STRUCTURED_TABLE_TAG_VALUE',

  // Sample controls errors and warnings that need special detection and handling
  SAMPLE_CONTROLS_IMPLICITLY_CHANGED = 'SAMPLE_CONTROLS_IMPLICITLY_CHANGED',

  // Name template usage warnings that need special detection and handling
  NAME_TEMPLATE_USAGE_WARNING = 'NAME_TEMPLATE_USAGE_WARNING',

  // Generic errors
  OTHER_ERROR = 'OTHER_ERROR',
}

export const LINK_TAG_ERROR_CODES = [
  RowErrorCode.NO_BATCHES_FOUND,
  RowErrorCode.NO_FILES_FOUND,
  RowErrorCode.MULTIPLE_FILES_FOUND,
  RowErrorCode.MULTIPLE_BATCHES_FOUND,
  RowErrorCode.INVALID_SELECTOR_OPTIONS,
];

export const NON_BLOCKING_ERROR_CODES = [
  RowErrorCode.IGNORED_COLUMN,
  RowErrorCode.NO_FILENAME,
  RowErrorCode.NO_BATCH_NAME,
  RowErrorCode.NO_STORABLE_NAME,
  RowErrorCode.INVALID_STORABLE_WARNING,
  RowErrorCode.SCHEMA_CHANGE_WARNING,
  RowErrorCode.NO_DATA_TABLE_ROW_FOUND,
  RowErrorCode.MULTIPLE_DATA_TABLE_ROWS_FOUND,
];

export const MAX_TIME_LIMIT_MINUTES = 30;

// Keep in sync with supported field types in benchling/bulk_adapters/schema_tags.py
export const PARSABLE_FIELD_TYPES = [
  SchemaFieldConstants.FIELD_TYPES.AA_PART_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.BASE_MOLECULE_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.ASSAY_REQUEST_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.ASSAY_RESULT_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.ASSAY_RUN_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.BATCH_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.BLOB_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.BIOENTITY_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.BOOLEAN.type,
  SchemaFieldConstants.FIELD_TYPES.DATE.type,
  SchemaFieldConstants.FIELD_TYPES.DATETIME.type,
  SchemaFieldConstants.FIELD_TYPES.ENTRY_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.FLOAT.type,
  SchemaFieldConstants.FIELD_TYPES.INTEGER.type,
  SchemaFieldConstants.FIELD_TYPES.LONG_TEXT.type,
  SchemaFieldConstants.FIELD_TYPES.PART_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.SELECTOR.type,
  SchemaFieldConstants.FIELD_TYPES.STORABLE_LINK.type,
  SchemaFieldConstants.FIELD_TYPES.TEXT.type,
  SchemaFieldConstants.FIELD_TYPES.TRANSLATION_LINK.type,
];

export const SpreadsheetBulkImportPreferences = {
  CAN_OVERRIDE_TIMEOUT: 'bulk_import.can_override_timeout',
  SOURCE_TYPE: 'bulk_import.source_type',
};

export const IMPORT_SOURCE_TYPE_TO_DISPLAY_TEXT = {
  [ImportSourceType.SPREADSHEET]: 'Spreadsheet',
  [ImportSourceType.RAW_TEXT]: 'Raw text',
  [ImportSourceType.SDF]: 'SDF',
};

export const EXCEL_FILE_EXTENSIONS = ['.xls', '.xlsx'];

export const ERRORS_TABLE_PAGINATION_LIMIT = 20;
